import { useEffect, useRef, useState } from 'react';
import { CircleAlertIcon, GlobeIcon } from 'lucide-react';
import type { WebResearchActivityDTO, WebResearchSynthesisStageDTO } from '@shared/schemas/web-tools';
import { ACTIVITY_LABEL_CLASS, Shimmer } from '@/components/ai-elements/shimmer';
import { cn } from '@/lib/utils';
import {
  formatConsultedSites,
  formatResearchDuration,
  getPersistedResearchDuration,
  getTypedPageCount,
  summarizeWebResearch,
  type WebActivityToolPart,
} from '../web-research-activity';
import './web-research-activity.css';

export type { WebActivityToolPart };

/**
 * Measures the live search duration on the client: from the moment the search
 * is observed running until it stops. A message that mounts already settled
 * (history reload) has no observed start, so no duration is ever invented.
 */
function useObservedSearchDuration(isSearching: boolean) {
  const startedAtRef = useRef<number | null>(null);
  const [durationMs, setDurationMs] = useState<number | null>(null);

  useEffect(() => {
    if (isSearching) {
      startedAtRef.current ??= Date.now();
      return;
    }

    if (startedAtRef.current !== null) {
      setDurationMs(Date.now() - startedAtRef.current);
      startedAtRef.current = null;
    }
  }, [isSearching]);

  return isSearching ? null : durationMs;
}

const SYNTHESIS_STAGE_LABELS: Record<WebResearchSynthesisStageDTO, string> = {
  drafting: 'Rédaction de la réponse…',
  reviewing: 'Vérification des sources…',
};

// Main status line: just below the answer text (16px); details one step lower.
const ROOT_CLASS_NAME = cn('flex max-w-2xl flex-col gap-0.5', ACTIVITY_LABEL_CLASS);
const DETAIL_CLASS_NAME = 'pl-6 text-sm leading-5';
const ICON_CLASS_NAME = 'size-4 shrink-0';

export function ChatWebActivity({
  activities = [],
  className,
  endedNormally = false,
  hasAnswerText = false,
  isStreaming = false,
  toolParts,
}: {
  activities?: WebResearchActivityDTO[];
  className?: string;
  /**
   * True when the turn finished with `finishReason: stop` (no abort, no error).
   * A phase left running then never reads as an interruption.
   */
  endedNormally?: boolean;
  /** True once the assistant started writing the answer text. */
  hasAnswerText?: boolean;
  isStreaming?: boolean;
  toolParts: WebActivityToolPart[];
}) {
  const research = summarizeWebResearch(activities, toolParts);
  // Research ends when the answer synthesis starts, or when answer text flows
  // with no tool call or research phase still running.
  const answerFlowing = hasAnswerText && !research.hasPendingWork;
  const isSearching = research.hasActivity
    && isStreaming
    && !research.synthesisStarted
    && !answerFlowing;
  const observedDurationMs = useObservedSearchDuration(isSearching);
  // The backend duration survives reloads; the client measure covers older backends.
  const durationMs = getPersistedResearchDuration(activities) ?? observedDurationMs;
  // Waiting state between the research and the first visible answer text.
  const synthesisStage = isStreaming && !hasAnswerText ? research.synthesisStage : null;

  if (!research.hasActivity) {
    return null;
  }

  const siteCount = research.sites.length > 0
    ? research.sites.length
    : getTypedPageCount(activities);
  const consultedLabel = siteCount > 0 ? formatConsultedSites(siteCount) : null;
  // A stream cut while research was still pending (cancellation, error) or
  // where every web call failed: never claim a clean completion.
  const isInterrupted = !isSearching && (
    (!isStreaming && !endedNormally && research.hasPendingWork)
    || (research.hasToolError && siteCount === 0)
  );

  if (isSearching) {
    return (
      <div className={cn(ROOT_CLASS_NAME, className)} data-state="searching">
        <div aria-live="polite" className="flex min-h-7 items-center gap-2" role="status">
          <GlobeIcon aria-hidden="true" className={cn(ICON_CLASS_NAME, 'text-muted-foreground')} />
          {/* transitions.dev "Shimmer text"; static under prefers-reduced-motion. */}
          <Shimmer>Recherche web en cours</Shimmer>
        </div>
        {research.currentSite ? (
          // Decorative live detail: kept out of the live region to avoid
          // announcing every page change.
          // Keyed by site so each new page replays the transitions.dev "Number
          // pop-in" recipe (replaces the previous phrase, never accumulates).
          <p
            aria-hidden="true"
            className={cn('t-digit-group is-animating text-muted-foreground', DETAIL_CLASS_NAME)}
            key={research.currentSite}
          >
            <span className="t-digit">Consultation de </span>
            <span className="t-digit" data-stagger="1" data-truncate="">{research.currentSite}</span>
          </p>
        ) : null}
      </div>
    );
  }

  // No fallback engine exists: when Staan is unavailable, say so plainly.
  const interruptedLabel = research.searchUnavailable && siteCount === 0
    ? 'Recherche web momentanément indisponible'
    : 'Recherche web interrompue';
  const summaryParts = [
    isInterrupted ? interruptedLabel : 'Recherche web terminée',
    consultedLabel,
  ].filter((part): part is string => part !== null);
  let summary = summaryParts.join(' · ');
  if (!isInterrupted && durationMs !== null) {
    summary += ` en ${formatResearchDuration(durationMs)}`;
  }

  const SummaryIcon = isInterrupted ? CircleAlertIcon : GlobeIcon;
  const stageLabel = synthesisStage ? SYNTHESIS_STAGE_LABELS[synthesisStage] : null;

  return (
    <div className={cn(ROOT_CLASS_NAME, className)} data-state={isInterrupted ? 'interrupted' : 'done'}>
      <div
        aria-live="polite"
        className={cn(
          'flex min-h-7 items-center gap-2',
          isInterrupted ? 'text-amber-700 dark:text-amber-500' : 'text-muted-foreground',
        )}
        role="status"
      >
        <SummaryIcon aria-hidden="true" className={ICON_CLASS_NAME} />
        <span>{summary}</span>
      </div>
      {/* Until the first answer text shows: the draft is written, then checked.
          The live region exists for the whole stream so each stage is announced. */}
      {isStreaming ? (
        <p aria-live="polite" className="pl-6" role="status">
          {stageLabel && synthesisStage ? (
            <Shimmer data-stage={synthesisStage} key={synthesisStage}>{stageLabel}</Shimmer>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
